import { useContext } from 'react';
import { FilePreviewContext } from '../context/FilePreviewContext';
import { FilePreviewContextType } from '../types';

/**
 * Hook to access FilePreview context
 * Must be used within a FilePreviewProvider
 *
 * @throws Error if used outside of FilePreviewProvider
 * @returns FilePreviewContextType
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { open, close, isOpen, isSupported } = useFilePreview();
 *
 *   const handleView = () => {
 *     if (isSupported(fileUrl)) {
 *       open(fileUrl, 'My Document');
 *     }
 *   };
 *
 *   return <button onClick={handleView}>View File</button>;
 * }
 * ```
 */
export const useFilePreview = (): FilePreviewContextType => {
  const context = useContext(FilePreviewContext);

  if (!context) {
    throw new Error(
      'useFilePreview must be used within a FilePreviewProvider. ' +
      'Wrap your component tree with <FilePreviewProvider>.'
    );
  }

  return context;
};

export default useFilePreview;
